import React, { useState, useEffect } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import en from "../language/Eng_Script.json";
import BottomMenu from "./Buttom_menu";
import Popup from "./popup";

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Noto Sans KR', sans-serif;
  }
`;

const HEADER_HEIGHT = "100vh";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const ScrollDownIndicator = styled.div`
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  z-index: 100;
  cursor: pointer;

  &::after {
    content: "▼";
    font-size: 24px;
    margin-top: 0;
    animation: ${bounce} 2s infinite;
  }
`;

const HomeContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

const MainVisualSection = styled.section`
  position: relative;
  width: 100%;
  height: ${HEADER_HEIGHT};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const SliderContainer = styled.div<{ $currentIndex: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 1s ease-in-out;
  transform: translateX(-${(props) => props.$currentIndex * 100}%);
  z-index: 0;
`;

const SlideImage = styled.div<{ $bgImage: string }>`
  min-width: 100%;
  height: 100%;
  background-image: url(${(props) => props.$bgImage});
  background-size: cover;
  background-position: center;
`;

const MainContent = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MainLogo = styled.img`
  height: 250px;
  margin-bottom: 30px;
  animation: ${bounce} 3s infinite;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const StorySection = styled.section`
  padding: 80px 60px;
  background-color: #f7f7f7;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
    padding: 60px 30px;
  }
`;

const TextContent = styled.div`
  max-width: 600px;
  margin-right: 80px;

  @media (max-width: 1200px) {
    margin-right: 0;
    margin-bottom: 40px;
  }
`;

const StoryTitle = styled.p`
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #888;
  margin-bottom: 5px;
`;

const BrewmanceTitle = styled.h2`
  font-family: "Georgia", serif;
  font-size: 4em;
  font-weight: 300;
  line-height: 1.1;
  color: #333;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 2.5em;
  }
`;

const StoryParagraph = styled.p`
  margin-bottom: 15px;
  line-height: 1.7;
  color: #555;
  white-space: pre-wrap;
`;

const StoryImage = styled.img`
  padding-top: 170px;
  width: 100%;
  max-width: 450px;
  height: auto;
`;

const ContentSection = styled.section`
  padding: 50px 60px;
  text-align: center;
  background-color: #ffffff;
  border-top: 1px solid #eee;

  h2 {
    font-size: 2.5em;
    color: #333;
    margin-bottom: 20px;
  }

  p {
    color: #666;
    margin-bottom: 30px;
  }

  img {
    width: 100%;
    max-width: 800px;
    height: auto;
    margin-top: 20px;
  }

  @media (max-width: 768px) {
    padding: 60px 30px;
  }
`;

const BoldTitle = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #333;
  margin-bottom: 15px;
  letter-spacing: 1px;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 20px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    width: 100%;
  }
`;

const BaseButton = styled.a`
  display: inline-block;
  padding: 12px 30px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;

  @media (max-width: 600px) {
    width: 200px;
    text-align: center;
  }
`;

const OutlineButton = styled(BaseButton)`
  color: #333 !important;
  background-color: transparent;
  border: 2px solid #333;

  &:visited {
    color: #333 !important;
  }

  &:hover {
    color: #ffffff !important;
    background-color: #333;
  }
`;

const SplitContainer = styled.div`
  width: 100%;
  height: 60vh;
  display: flex;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    height: 100vh;
  }
`;

const SplitPanel = styled.a<{ $bgImage: string }>`
  position: relative;
  flex: 1;
  background-image: url(${(props) => props.$bgImage});
  background-size: cover;
  background-position: center;
  transition: flex 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    transition: background-color 0.3s ease;
    z-index: 1;
  }

  &:hover {
    flex: 2.5;

    &::before {
      background-color: rgba(0, 0, 0, 0.2);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    flex: 1;

    &:hover {
      flex: 1.5;
    }
  }
`;

const PanelText = styled.div`
  position: relative;
  z-index: 2;
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

const BACKGROUND_IMAGES = [
  process.env.PUBLIC_URL + "assets/back_1.jpg",
  process.env.PUBLIC_URL + "assets/back_6.jpg",
  process.env.PUBLIC_URL + "assets/back_7.jpg",
  process.env.PUBLIC_URL + "assets/back_3.jpg",
  process.env.PUBLIC_URL + "assets/back_5.jpg",
];

const Home: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % BACKGROUND_IMAGES.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const scrollToStory = () => {
    const storySection = document.getElementById("story-section");
    if (storySection) {
      window.scrollTo({
        top: storySection.offsetTop,
        behavior: "smooth",
      });
    }
  };

  return (
    <HomeContainer>
      <GlobalStyle />
      {/*<Popup />*/}
      <MainVisualSection id="photoline">
        <SliderContainer $currentIndex={currentImageIndex}>
          {BACKGROUND_IMAGES.map((img, index) => (
            <SlideImage key={index} $bgImage={img} />
          ))}
        </SliderContainer>
        <MainContent>
          <MainLogo
            src={process.env.PUBLIC_URL + "assets/main_logo_1.png"}
            alt="Main Logo"
          />
        </MainContent>
        <ScrollDownIndicator onClick={scrollToStory}></ScrollDownIndicator>
      </MainVisualSection>

      <StorySection id="story-section">
        <TextContent>
          <StoryTitle>{en.about.story.title}</StoryTitle>
          <BrewmanceTitle>Firestone Sydney</BrewmanceTitle>
          <StoryParagraph>{en.about.story.paragraph1}</StoryParagraph>
          <StoryParagraph>{en.about.story.paragraph2}</StoryParagraph>
          <StoryParagraph>{en.about.story2.paragraph2}</StoryParagraph>
          <StoryParagraph>{en.about.story2.paragraph3}</StoryParagraph>
        </TextContent>
        <StoryImage
          src={process.env.PUBLIC_URL + "/assets/main_logo_2.png"}
          alt="Buttered Illustration"
        />
      </StorySection>

      <ContentSection style={{ padding: 0, borderTop: "none" }}>
        <SplitContainer>
          <SplitPanel href="/hornsby" $bgImage="assets/new_image/store2.webp">
            <PanelText>Hornsby</PanelText>
          </SplitPanel>

          <SplitPanel href="/eastwood" $bgImage="assets/new_image/store2.webp">
            <PanelText>Eastwood</PanelText>
          </SplitPanel>
        </SplitContainer>
      </ContentSection>

      <BottomMenu />
    </HomeContainer>
  );
};

export default Home;
